import React from "react";
import { Document, Page } from "@react-pdf/renderer";
import { ReportHeader } from "../components/charting/pdf/shared/ReportHeader";
import { ReportFooter } from "../components/charting/pdf/shared/ReportFooter";
import { ReportTable } from "../components/charting/pdf/shared/ReportTable";
import { StrikeZoneGrid } from "../components/charting/pdf/shared/StrikeZoneGrid";
import { reportStyles } from "../components/charting/pdf/shared/styles";
import { calculatePitchMetrics } from "../components/charting/pdf/shared/pitchProcessing";
import { toTitleCase } from "../components/charting/pdf/shared/utils";

export const BullpenReportPDF = ({ charts = [] }) => {
  const pitcherData = charts.reduce((acc, chart) => {
    const pitcherName = chart.pitcher?.name || "Unknown Pitcher";
    if (!acc[pitcherName]) {
      acc[pitcherName] = {
        charts: [],
        latestDate: chart.date,
      };
    }
    acc[pitcherName].charts.push(chart);
    if (new Date(chart.date) > new Date(acc[pitcherName].latestDate)) {
      acc[pitcherName].latestDate = chart.date;
    }
    return acc;
  }, {});

  return (
    <Document>
      {Object.entries(pitcherData).map(([pitcher, { charts, latestDate }]) => {
        const allPitches = charts.flatMap((chart) =>
          (chart.pitches || []).map((pitch) => ({
            ...pitch,
            zoneType: chart.zoneType,
          }))
        );

        const pitchTypes = allPitches.reduce((acc, pitch) => {
          const type = pitch.type?.toUpperCase() || "UNKNOWN";
          if (!acc[type]) acc[type] = [];
          acc[type].push(pitch);
          return acc;
        }, {});

        const pitchTypeRows = Object.entries(pitchTypes).map(
          ([type, pitches]) => ({
            type,
            ...calculatePitchMetrics(pitches, allPitches.length),
            pitches,
            isAll: false,
          })
        );

        const allMetrics = calculatePitchMetrics(allPitches, allPitches.length);
        
        const processedTypes = [
          ...pitchTypeRows,
          {
            type: "ALL",
            count: allMetrics.count,
            usage: "100.0",
            avgVelo: allMetrics.avgVelo,
            maxVelo: allMetrics.maxVelo,
            strikeRate: allMetrics.strikeRate,
            accuracy: allMetrics.accuracy,
            avgMissDistance: allMetrics.avgMissDistance,
            pitches: allPitches,
            isAll: true,
          },
        ];

        const chunks = processedTypes.reduce((acc, type, i) => {
          const chunkIndex = Math.floor(i / 6);
          if (!acc[chunkIndex]) acc[chunkIndex] = [];
          acc[chunkIndex].push(type);
          return acc;
        }, []);

        return chunks.map((chunk, pageIndex) => {
          const tableRows = chunk.map(({ type, count, usage, avgVelo, maxVelo, strikeRate, accuracy, avgMissDistance, isAll }) => ({
            cells: [
              { value: toTitleCase(type) },
              { value: count },
              { value: `${usage}%` },
              { value: avgVelo },
              { value: maxVelo },
              { value: `${strikeRate}%` },
              { value: `${accuracy}%` },
              { value: `${avgMissDistance} in.` },
            ],
            backgroundColor: isAll ? "#E5E7EB" : undefined,
            cellStyles: isAll ? Array(8).fill({ fontWeight: "bold" }) : undefined,
          }));

          const strikeZoneData = chunk
            .filter(({ isAll }) => !isAll)
            .reduce((acc, { type, pitches }) => {
              acc[type] = { pitches, isAll: false };
              return acc;
            }, {});

          return (
            <Page
              key={`${pitcher}-${pageIndex}`}
              size="A4"
              orientation="portrait"
              style={reportStyles.page}
            >
              <ReportHeader
                title={`${pitcher} - Bullpen Report`}
                date={new Date(latestDate).toLocaleDateString()}
              />

              <ReportTable
                headers={[
                  "Pitch Type",
                  "Count",
                  "Usage %",
                  "Avg Velo",
                  "Max Velo",
                  "Strike%",
                  "Accuracy%",
                  "Avg. Miss",
                ]}
                rows={tableRows}
                cellWidths={["12.5%", "10%", "12.5%", "12.5%", "12.5%", "10%", "15%", "15%"]}
              />

              <StrikeZoneGrid pitchData={strikeZoneData} showAll={false} />

              <ReportFooter />
            </Page>
          );
        });
      })}
    </Document>
  );
};

export const BullpenReport = BullpenReportPDF;
export default BullpenReportPDF;